import { styled } from 'styled-components';

import { Divider } from '#/components/Divider/Divider';
import { FilestackImage } from '#/components/FilestackImage/FilestackImage';
import { Icon } from '#/components/Icon/Icon';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { BodySmall } from '#/components/Text/BodySmall';
import { H3 } from '#/components/Text/H3';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { CmsApiCategoryHighlightBlockItem } from '#/modules/cms/api/types/blocks/CmsApiCategoryHighlightBlock';
import { colors, transition } from '#/theme/theme';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

import { CmsCategoryHighlightBlockCategory_Box_1 } from './CmsCategoryHighlightBlockCategory.styled';

const ImageContainer = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
`;

const StyledLink = styled(StyledNativeLinkWithRef)<{ $color?: string }>`
  color: ${(props) => props.$color};

  svg {
    transition: transform ${transition};

    path,
    polyline {
      fill: currentcolor;
    }
  }

  &:hover,
  &:focus {
    &,
    * {
      color: ${(props) => props.$color};
      text-decoration: underline;
    }

    svg {
      transform: translateX(4px);
    }
  }
`;

type Props = {
  homePageUrl: string | undefined;
  category: CmsApiCategoryHighlightBlockItem;
  blockIndex: number;
  pageUpdated: string;
};

export function CmsCategoryHighlightBlockCategory({
  category,
  homePageUrl,
  blockIndex,
  pageUpdated,
}: Props) {
  const { i18n } = useI18n();

  return (
    <CmsCategoryHighlightBlockCategory_Box_1
      data-qa-id={`category-highlight-${category.slug}`}
    >
      {category.image ? (
        <div
          style={{
            position: 'relative',
            height: 96,
            width: 96,
            marginBottom: '24px',
          }}
        >
          <ImageContainer>
            <FilestackImage
              alt={category.image.altText ?? ''}
              data={category.image}
              qaId={`image-${category.slug}`}
            />
          </ImageContainer>
        </div>
      ) : null}
      <H3 style={{ color: colors.lightContentGrey, marginBlock: '16px' }}>
        {category.name}
      </H3>

      <BodySmall style={{ lineHeight: '1.25' }}>
        {category.description}
      </BodySmall>

      <Divider marginBottom={24} marginTop={16} />

      {category.pages.map((page, index) => {
        const pageTitleWords = page.title.split(' ');
        return (
          <BodySmall
            styleAs="h4"
            key={page.id || index}
            style={{ lineHeight: '1.25', marginBlock: '16px' }}
          >
            <StyledLink
              qaId="articles-link"
              href={`/${page.url}`}
              onClick={() => {
                trackClicked('CMS Block', {
                  block_position: blockIndex,
                  block_type: 'Category Highlight',
                  cta_type: 'Link',
                  page_updated: pageUpdated,
                });
              }}
            >
              {pageTitleWords.slice(0, pageTitleWords.length - 1).join(' ')}{' '}
              <div style={{ display: 'inline-flex' }}>
                {pageTitleWords
                  .slice(pageTitleWords.length - 1, pageTitleWords.length)
                  .join(' ')}

                <div style={{ display: 'inline-flex', marginLeft: '8px' }}>
                  <Icon name="arrow-right" size={24} />
                </div>
              </div>
            </StyledLink>
          </BodySmall>
        );
      })}
      {homePageUrl && (
        <BodySmall
          styleAs="h5"
          style={{ fontSize: 20, lineHeight: '1.25', marginBottom: '8px' }}
        >
          <StyledLink
            $color={colors.lightContentGrey}
            qaId={`articles-link-${category.slug}`}
            href={`/${homePageUrl}?categories=${category.slug}&pq=`}
            onClick={() => {
              trackClicked('CMS Block', {
                block_position: blockIndex,
                block_type: 'Category Highlight',
                cta_type: 'Link',
                page_updated: pageUpdated,
              });
            }}
          >
            <span style={{ display: 'inline-flex' }}>
              {i18n('See all')}

              <div style={{ marginLeft: '8px', display: 'inline-flex' }}>
                <Icon name="arrow-right" size={24} />
              </div>
            </span>
          </StyledLink>
        </BodySmall>
      )}
    </CmsCategoryHighlightBlockCategory_Box_1>
  );
}
